/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Chat & Multi-Turn Conversations DTOs & Validation Schemas (OpenAPI 3.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * System Pre-configured AI Bot Persona Schema.
 */
export const ChatPersonaSchema = z
  .object({
    id: z.string().openapi({ description: 'Unique Persona identifier', example: 'persona_tutor_01' }),
    name: z.string().min(1).openapi({ description: 'Persona display name', example: 'Academic Tutor' }),
    slug: z.string().min(1).openapi({ description: 'Unique slug identifier', example: 'academic-tutor' }),
    avatarUrl: z.string().nullable().optional().openapi({ description: 'Avatar image URL', example: 'https://assets.sophiaplatform.com/personas/tutor.png' }),
    systemPrompt: z.string().min(1).openapi({ description: 'Persona system instruction prompt', example: 'You are an encouraging academic tutor helping students master complex subjects.' }),
    firstMessage: z.string().nullable().optional().openapi({ description: 'Initial greeting message', example: 'Hello! What subject would you like to explore today?' }),
    sortOrder: z.number().int().default(0).openapi({ description: 'Display sorting priority', example: 1 }),
  })
  .openapi('ChatPersona');

export type ChatPersona = z.infer<typeof ChatPersonaSchema>;

/**
 * Persona List Response.
 */
export const ChatPersonaListResponseSchema = z
  .object({
    personas: z.array(ChatPersonaSchema).openapi({ description: 'Available system personas' }),
    total: z.number().int().openapi({ description: 'Total number of personas', example: 5 }),
  })
  .openapi('ChatPersonaListResponse');

export type ChatPersonaListResponse = z.infer<typeof ChatPersonaListResponseSchema>;

/**
 * Custom Bot Profile Update Request Schema.
 */
export const UpdateCustomBotRequestSchema = z
  .object({
    name: z.string().min(1).max(100).optional().openapi({ description: 'Bot display name', example: 'Customer Support Lead' }),
    systemPrompt: z.string().min(1).max(10000).optional().openapi({ description: 'System personality & directives', example: 'You are a warm, knowledgeable support representative.' }),
    welcomeMessage: z.string().max(1000).optional().openapi({ description: 'Initial message greeted upon opening widget', example: 'Hello! How can I help you today?' }),
    allowedDomains: z.array(z.string()).optional().openapi({ description: 'Whitelisted domains for embedded widget', example: ['myshop.com', '*.myshop.com'] }),
    widgetTheme: z.record(z.unknown()).optional().openapi({ description: 'Custom CSS theme tokens', example: { primaryColor: '#6366f1', position: 'bottom-right' } }),
  })
  .openapi('UpdateCustomBotRequest');

export type UpdateCustomBotRequest = z.infer<typeof UpdateCustomBotRequestSchema>;

/**
 * Create Conversation Session Request Schema.
 */
export const CreateConversationRequestSchema = z
  .object({
    title: z.string().max(200).optional().openapi({ description: 'Optional custom conversation title', example: 'Debugging Cloudflare Workers' }),
    personaId: z.string().optional().openapi({ description: 'Optional System Persona ID', example: 'persona_coder_01' }),
    customBotId: z.string().uuid().optional().openapi({ description: 'Optional Custom RAG Bot / Knowledge Base ID', example: '123e4567-e89b-12d3-a456-426614174001' }),
  })
  .openapi('CreateConversationRequest');

export type CreateConversationRequest = z.infer<typeof CreateConversationRequestSchema>;

/**
 * Update Conversation Session Request Schema.
 */
export const UpdateConversationRequestSchema = z
  .object({
    title: z.string().min(1).max(200).openapi({ description: 'Updated conversation title', example: 'Cloudflare Vectorize Deep Dive' }),
  })
  .openapi('UpdateConversationRequest');

export type UpdateConversationRequest = z.infer<typeof UpdateConversationRequestSchema>;

/**
 * Conversation Session Response Schema.
 */
export const ConversationResponseSchema = z
  .object({
    id: z.string().uuid().openapi({ description: 'Unique conversation thread UUID', example: '123e4567-e89b-12d3-a456-426614174010' }),
    workspaceId: z.string().uuid().openapi({ description: 'Workspace UUID', example: '123e4567-e89b-12d3-a456-426614174000' }),
    userId: z.string().uuid().openapi({ description: 'Owner User UUID', example: '123e4567-e89b-12d3-a456-426614174099' }),
    personaId: z.string().nullable().optional().openapi({ description: 'Linked System Persona ID', example: 'persona_coder_01' }),
    customBotId: z.string().nullable().optional().openapi({ description: 'Linked Custom Bot ID', example: '123e4567-e89b-12d3-a456-426614174001' }),
    title: z.string().openapi({ description: 'Conversation title', example: 'Debugging Cloudflare Workers' }),
    totalMessages: z.number().int().default(0).openapi({ description: 'Total message count in thread', example: 12 }),
    createdAt: z.string().openapi({ description: 'ISO creation timestamp', example: '2026-10-20T10:00:00.000Z' }),
    updatedAt: z.string().openapi({ description: 'ISO update timestamp', example: '2026-10-20T10:05:00.000Z' }),
  })
  .openapi('ConversationResponse');

export type ConversationResponse = z.infer<typeof ConversationResponseSchema>;

/**
 * Conversation List Response Schema.
 */
export const ConversationListResponseSchema = z
  .object({
    conversations: z.array(ConversationResponseSchema).openapi({ description: 'List of conversation threads' }),
    total: z.number().int().openapi({ description: 'Total conversations matching query', example: 1 }),
  })
  .openapi('ConversationListResponse');

export type ConversationListResponse = z.infer<typeof ConversationListResponseSchema>;

/**
 * Chat Message Role Enum.
 */
export const ChatMessageRoleEnum = z
  .enum(['user', 'assistant', 'system'])
  .openapi({ description: 'Sender role of the message', example: 'user' });

export type ChatMessageRole = z.infer<typeof ChatMessageRoleEnum>;

/**
 * Chat Message Item Schema.
 */
export const ChatMessageSchema = z
  .object({
    id: z.string().uuid().openapi({ description: 'Unique message UUID', example: '123e4567-e89b-12d3-a456-426614174020' }),
    sessionId: z.string().uuid().openapi({ description: 'Associated conversation session UUID', example: '123e4567-e89b-12d3-a456-426614174010' }),
    role: ChatMessageRoleEnum,
    content: z.string().openapi({ description: 'Message textual content', example: 'Can you explain how Vectorize indexes vectors?' }),
    attachments: z.array(z.unknown()).default([]).openapi({ description: 'JSON media attachments or citations', example: [] }),
    tokensUsed: z.number().int().default(0).openapi({ description: 'Tokens consumed by this message', example: 45 }),
    modelCode: z.string().nullable().optional().openapi({ description: 'Model used for generating this message', example: 'gpt-4o' }),
    createdAt: z.string().openapi({ description: 'ISO timestamp when message was recorded', example: '2026-10-20T10:01:00.000Z' }),
  })
  .openapi('ChatMessage');

export type ChatMessage = z.infer<typeof ChatMessageSchema>;

/**
 * Chat Message History Response Schema.
 */
export const ChatMessageHistoryResponseSchema = z
  .object({
    sessionId: z.string().uuid().openapi({ description: 'Session UUID', example: '123e4567-e89b-12d3-a456-426614174010' }),
    messages: z.array(ChatMessageSchema).openapi({ description: 'Chronological list of conversation messages' }),
    total: z.number().int().openapi({ description: 'Total number of messages in history', example: 6 }),
  })
  .openapi('ChatMessageHistoryResponse');

export type ChatMessageHistoryResponse = z.infer<typeof ChatMessageHistoryResponseSchema>;

/**
 * Send Message & Stream Response Request Schema.
 */
export const SendChatMessageStreamRequestSchema = z
  .object({
    message: z.string().min(1).openapi({ description: 'User input question or instruction', example: 'How do Cloudflare Workers handle vector search?' }),
    model: z.string().default('gpt-4o-mini').openapi({ description: 'AI Model code identifier', example: 'gpt-4o-mini' }),
    temperature: z.number().min(0).max(2).optional().default(0.7).openapi({ description: 'Sampling temperature', example: 0.7 }),
    maxTokens: z.number().int().min(1).max(8192).optional().default(1000).openapi({ description: 'Maximum completion tokens', example: 1000 }),
  })
  .openapi('SendChatMessageStreamRequest');

export type SendChatMessageStreamRequest = z.infer<typeof SendChatMessageStreamRequestSchema>;

/**
 * Public Bot Config Response Schema for Embeddable Widget.
 */
export const PublicBotConfigResponseSchema = z
  .object({
    id: z.string().uuid().openapi({ description: 'Bot UUID', example: '123e4567-e89b-12d3-a456-426614174001' }),
    name: z.string().openapi({ description: 'Bot display name', example: 'Technical Support Assistant' }),
    welcomeMessage: z.string().nullable().optional().openapi({ description: 'Initial greeting message', example: 'Hello! How can I help you today?' }),
    avatarUrl: z.string().nullable().optional().openapi({ description: 'Bot avatar URL', example: 'https://assets.sophiaplatform.com/bot-avatar.png' }),
    widgetTheme: z.record(z.unknown()).default({}).openapi({ description: 'Custom theming parameters' }),
    allowedDomains: z.array(z.string()).default(['*']).openapi({ description: 'Whitelisted embedding domains' }),
  })
  .openapi('PublicBotConfigResponse');

export type PublicBotConfigResponse = z.infer<typeof PublicBotConfigResponseSchema>;

/**
 * Chatbot Widget Lead Capture Request Schema.
 */
export const BotLeadCaptureRequestSchema = z
  .object({
    name: z.string().min(1).max(100).openapi({ description: 'Full name of potential customer', example: 'Alex Morgan' }),
    email: z.string().email().openapi({ description: 'Business email of potential customer', example: 'alex@example.com' }),
    phone: z.string().max(20).optional().openapi({ description: 'Optional phone number', example: '+1 (555) 234-5678' }),
    metadata: z.record(z.unknown()).optional().openapi({ description: 'Optional client metadata like referrer or page URL' }),
  })
  .openapi('BotLeadCaptureRequest');

export type BotLeadCaptureRequest = z.infer<typeof BotLeadCaptureRequestSchema>;

/**
 * Chatbot Widget Lead Capture Response Schema.
 */
export const BotLeadCaptureResponseSchema = z
  .object({
    success: z.boolean().default(true).openapi({ description: 'Lead registration status', example: true }),
    leadId: z.string().openapi({ description: 'Generated Lead UUID', example: '123e4567-e89b-12d3-a456-426614174090' }),
    sessionId: z.string().uuid().openapi({ description: 'Dedicated guest chat session UUID', example: '123e4567-e89b-12d3-a456-426614174010' }),
  })
  .openapi('BotLeadCaptureResponse');

export type BotLeadCaptureResponse = z.infer<typeof BotLeadCaptureResponseSchema>;

/**
 * Public Chatbot Widget Streaming Request Schema.
 */
export const PublicWidgetChatStreamRequestSchema = z
  .object({
    message: z.string().min(1).max(4000).openapi({ description: 'Visitor message or question', example: 'What plans do you offer?' }),
    sessionId: z.string().uuid().optional().openapi({ description: 'Existing conversation session ID', example: '123e4567-e89b-12d3-a456-426614174010' }),
    leadId: z.string().optional().openapi({ description: 'Identified lead ID if captured', example: '123e4567-e89b-12d3-a456-426614174090' }),
  })
  .openapi('PublicWidgetChatStreamRequest');

export type PublicWidgetChatStreamRequest = z.infer<typeof PublicWidgetChatStreamRequestSchema>;

